import { useCallback, useEffect, useRef, useState } from 'react';
import * as cocoSsd from '@tensorflow-models/coco-ssd';
import '@tensorflow/tfjs';

let cocoModelPromise = null;

const loadCocoModel = () => {
  if (!cocoModelPromise) {
    cocoModelPromise = cocoSsd.load();
  }
  return cocoModelPromise;
};

const defaultConstraints = {
  audio: false,
  video: { facingMode: 'user', width: 640, height: 360 },
};

const createHiddenVideo = () => {
  const video = document.createElement('video');
  video.setAttribute('playsinline', 'true');
  video.muted = true;
  video.autoplay = true;
  video.playsInline = true;
  video.style.position = 'fixed';
  video.style.width = '1px';
  video.style.height = '1px';
  video.style.opacity = '0';
  video.style.pointerEvents = 'none';
  video.style.left = '-9999px';
  video.style.top = '-9999px';
  video.dataset.webcamProctor = 'true';
  return video;
};

const enrichDetections = (predictions, timestamp) =>
  predictions.map((prediction) => ({ ...prediction, timestamp }));

export const useWebcamDetections = (options = {}) => {
  const {
    enabled = true,
    constraints = defaultConstraints,
    detectionInterval = 0,
    onDetections,
    videoElement: externalVideoElement,
  } = options;

  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [detections, setDetections] = useState([]);

  const modelRef = useRef(null);
  const streamRef = useRef(null);
  const internalVideoRef = useRef(null);
  const rafRef = useRef(null);
  const startPromiseRef = useRef(null);
  const lastDetectionRef = useRef(0);
  const onDetectionsRef = useRef(onDetections);
  const mountedRef = useRef(true);

  useEffect(() => {
    onDetectionsRef.current = onDetections;
  }, [onDetections]);

  const cancelAnimation = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const teardownVideo = useCallback(() => {
    cancelAnimation();
    const video = externalVideoElement || internalVideoRef.current;
    if (video) {
      try {
        if (video.srcObject) {
          video.srcObject.getTracks().forEach((track) => track.stop());
        }
        video.srcObject = null;
        if (!externalVideoElement && video.parentNode) {
          video.parentNode.removeChild(video);
        }
      } catch (teardownError) {
        console.error('Failed to teardown webcam stream', teardownError);
      }
    }
    if (!externalVideoElement) {
      internalVideoRef.current = null;
    }
    streamRef.current = null;
    modelRef.current = null;
    lastDetectionRef.current = 0;
  }, [cancelAnimation, externalVideoElement]);

  const runDetectionLoop = useCallback(() => {
    const video = externalVideoElement || internalVideoRef.current;
    const model = modelRef.current;
    if (!video || !model) {
      return;
    }

    const loop = () => {
      if (!mountedRef.current) {
        cancelAnimation();
        return;
      }

      if (!video.srcObject || video.readyState < 2) {
        rafRef.current = requestAnimationFrame(loop);
        return;
      }

      const now = Date.now();
      if (detectionInterval > 0 && now - lastDetectionRef.current < detectionInterval) {
        rafRef.current = requestAnimationFrame(loop);
        return;
      }

      model
        .detect(video)
        .then((predictions) => {
          if (!mountedRef.current) {
            return;
          }
          const timestamp = Date.now();
          lastDetectionRef.current = timestamp;
          const enriched = enrichDetections(predictions, timestamp);
          setDetections(enriched);
          if (typeof onDetectionsRef.current === 'function') {
            onDetectionsRef.current(enriched);
          }
        })
        .catch((detectError) => {
          console.error('Webcam detection error', detectError);
        })
        .finally(() => {
          rafRef.current = requestAnimationFrame(loop);
        });
    };

    cancelAnimation();
    rafRef.current = requestAnimationFrame(loop);
  }, [cancelAnimation, detectionInterval, externalVideoElement]);

  const start = useCallback(async () => {
    if (!mountedRef.current) {
      return;
    }

    if (startPromiseRef.current) {
      await startPromiseRef.current;
      return;
    }

    if (!enabled) {
      return;
    }

    startPromiseRef.current = (async () => {
      try {
        setError(null);
        setStatus('starting');

        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Browser does not support webcam access.');
        }

        const model = await loadCocoModel();
        modelRef.current = model;

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;

        let video = externalVideoElement || internalVideoRef.current;
        if (!video) {
          video = createHiddenVideo();
          document.body.appendChild(video);
          internalVideoRef.current = video;
        }

        video.srcObject = stream;

        await new Promise((resolve) => {
          const handleLoaded = () => {
            video.removeEventListener('loadeddata', handleLoaded);
            resolve();
          };
          if (video.readyState >= 2) {
            resolve();
            return;
          }
          video.addEventListener('loadeddata', handleLoaded);
        });

        if (video.paused) {
          await video.play().catch(() => undefined);
        }

        if (mountedRef.current) {
          setStatus('running');
          runDetectionLoop();
        }
      } catch (startError) {
        console.error('Failed to start webcam detection', startError);
        if (mountedRef.current) {
          setError(startError);
          setStatus('error');
        }
        teardownVideo();
      } finally {
        startPromiseRef.current = null;
      }
    })();

    await startPromiseRef.current;
  }, [constraints, enabled, externalVideoElement, runDetectionLoop, teardownVideo]);

  const stop = useCallback(() => {
    cancelAnimation();
    if (startPromiseRef.current) {
      startPromiseRef.current = null;
    }
    teardownVideo();
    if (mountedRef.current) {
      setDetections([]);
      setStatus('idle');
    }
  }, [cancelAnimation, teardownVideo]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stop();
    };
  }, [stop]);

  useEffect(() => {
    if (enabled) {
      start();
    } else {
      stop();
    }
  }, [enabled, start, stop]);

  return {
    detections,
    status,
    error,
    videoElement: externalVideoElement || internalVideoRef.current,
    start,
    stop,
  };
};

export default useWebcamDetections;